/**
 * 指令门禁：把"现在能不能发这条命令"和"能发的话该怎么说、有什么要注意的"
 * 集中在一处，产出的全是**中文原因**，供界面做禁用提示与二次确认。
 *
 * 三条设计线：
 *
 * 1. **只给原因，不弹窗。** 管理器不认识界面：它不知道用户点的是哪个按钮、
 *    有没有已打开的确认框。把"为什么不能发"表达成数据，界面才能自由决定
 *    是灰掉按钮、悬浮提示还是弹确认框。
 *
 * 2. **拦截与警告分开。** "未解锁就起飞"必须拦住；"坐标会以 float32 下发"
 *    只能提醒 —— 后者在绝大多数真机上都成立（很多固件根本不上报
 *    `COMMAND_INT`），拿它拦人等于让功能不可用。
 *
 * 3. **不自己发明危险等级，也不自己发明机型支持。** 两者都来自
 *    `@hgc/ardupilot` 的 `MAV_CMD` 元数据（上游 XML + 逐条核对过的中文标注）。
 *    本文件只补充**协议层面**能判定的前置条件（解锁状态、位置估计、命令编号冲突），
 *    因为这些既不在上游元数据里，也不属于任何机型知识。
 */

import {
  COMMAND_RISK_LABELS,
  commandId,
  isSupportedBy,
  needsConfirmation,
  vehicleKindOf,
  type CommandMeta,
  type VehicleKind,
} from '@hgc/ardupilot';

import type { VehicleState } from '../vehicle';
import type { CommandBlocker, CommandRequest } from './types';

/** 一组命令编号。名字→编号一律查 `@hgc/ardupilot`，本文件不抄任何数字。 */
function idSet(names: readonly string[]): ReadonlySet<number> {
  const ids = new Set<number>();
  for (const name of names) {
    const id = commandId(name);
    // 上游删掉某条命令时这里会静默少一项 —— 这是可接受的：少了它只是少一条
    // 本地门禁，而抄错编号会去拦错误的命令。所以宁可少，不可错。
    if (id !== undefined) ids.add(id);
  }
  return ids;
}

/**
 * 只在**已解锁**状态下才有意义的命令。
 *
 * 判据是"未解锁时下发一定没有效果"，而不是"解锁时才有危险"：
 * 前者是确定性的事实（ArduPilot 里这些命令的入口都带 `!motors->armed()` 之类的检查），
 * 后者要靠机型知识，而那份知识在 `ardupilot` 包的 risk 字段里。
 */
const ARMED_REQUIRED: ReadonlySet<number> = idSet([
  'MAV_CMD_NAV_TAKEOFF',
  'MAV_CMD_NAV_VTOL_TAKEOFF',
  'MAV_CMD_NAV_LAND',
  'MAV_CMD_NAV_RETURN_TO_LAUNCH',
  'MAV_CMD_MISSION_START',
  'MAV_CMD_DO_VTOL_TRANSITION',
  'MAV_CMD_DO_GUIDED_MASTER',
]);

/**
 * 只在**未解锁**状态下允许的命令。
 *
 * 这些是"上锁会保护你"的典型：电机测试、舵机测试、传感器校准、刷引导程序。
 * 在已解锁时下发它们，最好的情况是被飞控拒绝，最坏的情况是螺旋桨在没有预警的
 * 情况下转起来 —— 所以宁可在本地就拦下并说清原因。
 */
const DISARMED_REQUIRED: ReadonlySet<number> = idSet([
  'MAV_CMD_DO_MOTOR_TEST',
  'MAV_CMD_ACTUATOR_TEST',
  'MAV_CMD_CONFIGURE_ACTUATOR',
  'MAV_CMD_PREFLIGHT_CALIBRATION',
  'MAV_CMD_ACCELCAL_VEHICLE_POS',
  'MAV_CMD_DO_START_MAG_CAL',
  'MAV_CMD_FIXED_MAG_CAL',
  'MAV_CMD_FIXED_MAG_CAL_YAW',
  'MAV_CMD_SET_FACTORY_TEST_MODE',
  'MAV_CMD_FLASH_BOOTLOADER',
  'MAV_CMD_DO_ACCEPT_MAG_CAL',
]);

/**
 * 需要飞控**已有位置估计**的命令。
 *
 * 上游元数据里没有"需要定位"这个属性（`hasLocation` 说的是"命令带坐标参数"，
 * 而这两件事并不等价：`NAV_TAKEOFF` 可以不指定坐标，但仍然必须有位置估计）。
 * 所以这份清单是本地逐条列出来的，且刻意只收"没有位置估计就毫无意义"的那些。
 */
const POSITION_REQUIRED: ReadonlySet<number> = idSet([
  'MAV_CMD_NAV_TAKEOFF',
  'MAV_CMD_NAV_VTOL_TAKEOFF',
  'MAV_CMD_NAV_LAND',
  'MAV_CMD_NAV_RETURN_TO_LAUNCH',
  'MAV_CMD_NAV_WAYPOINT',
  'MAV_CMD_NAV_SPLINE_WAYPOINT',
  'MAV_CMD_NAV_LOITER_UNLIM',
  'MAV_CMD_NAV_LOITER_TURNS',
  'MAV_CMD_NAV_LOITER_TIME',
  'MAV_CMD_NAV_LOITER_TO_ALT',
  'MAV_CMD_DO_REPOSITION',
  'MAV_CMD_DO_ORBIT',
  'MAV_CMD_DO_SET_HOME',
]);

/** 链路状态的中文名。链路层自己只给枚举，中文文案是界面的事（这里先给一份共用的）。 */
const LINK_STATE_LABELS = {
  idle: '未连接',
  connecting: '连接中',
  online: '已连接',
  silent: '链路已打开但收不到遥测',
  offline: '链路已断开（收不到心跳）',
  error: '链路错误',
} as const;

/** 从状态快照推断机型类别。没收到心跳时是 `'unknown'` —— 那是"不知道"，不是"不支持"。 */
export function vehicleKindOfState(state: VehicleState): VehicleKind {
  // MAV_TYPE 是唯一权威来源；vehicleClass 只是它的粗分类，会丢掉 tracker/blimp 的区别
  return state.type === null ? 'unknown' : vehicleKindOf(state.type);
}

export interface CommandGateContext {
  readonly state: VehicleState;
  /** 车型类别（判断机型支持用） */
  readonly kind: VehicleKind;
  /** 命令元数据；编号不在上游表里时为 `null` */
  readonly meta: CommandMeta | null;
  readonly request: CommandRequest;
  /** 飞控是否上报了 `COMMAND_INT` 能力 */
  readonly commandIntAvailable: boolean;
  /** 是否已有同编号的命令在飞（ACK 无法归属） */
  readonly commandNumberBusy: boolean;
  readonly enforceConfirmation: boolean;
  readonly disposed: boolean;
}

export interface GateEvaluation {
  readonly blockers: readonly CommandBlocker[];
  readonly warnings: readonly string[];
  /** 这次下发是否会走 `COMMAND_INT` */
  readonly viaInt: boolean;
}

/**
 * 判断是否值得为这条命令改用 `COMMAND_INT`。
 *
 * **权衡（重要）**：`COMMAND_INT` 的 x/y 是 `int32` 的"度 × 1e7"，而 `COMMAND_LONG`
 * 的经纬度是 `float32` —— 后者只有约 7 位有效数字，121.4737012 这种经度会被舍到
 * 米级（float32 在这个量级上的步长约 1e-5 度 ≈ 1.1 米）。这正是本仓库内部
 * 一律用 ×1e7 整数的原因。所以带经纬度的命令优先走 `COMMAND_INT`。
 *
 * 代价是 `COMMAND_INT` **没有 `confirmation` 字段**（上游定义如此，不是我们漏填）：
 * 走 INT 的命令在协议层面无法标记"这是重传"。这里的取舍是"精度优先"——
 * 带坐标的命令几乎都是幂等的（飞到某点、设置返航点），重复执行一次的后果
 * 远小于坐标偏差一米的后果。链路丢包导致的重复执行风险，靠
 * "同编号命令串行 + 重传次数上限"控制在很小的范围内。
 *
 * 能力位缺失时退化为 `COMMAND_LONG`，并通过 `warnings` 如实告知调用方精度损失 ——
 * 界面应当把这个警告显示出来，而不是自己决定要不要在乎。
 */
export function evaluateGate(context: CommandGateContext): GateEvaluation {
  const { state, kind, meta, request } = context;
  const blockers: CommandBlocker[] = [];
  const warnings: string[] = [];

  if (context.disposed) {
    blockers.push({ code: 'disposed', text: '管理器已销毁，不再接受新指令' });
  }

  if (meta === null) {
    const unknown = typeof request.command === 'number' ? `${request.command}` : String(request.command);
    blockers.push({
      code: 'unknown-command',
      text: `编号 ${unknown} 不在上游 MAV_CMD 表里（可能是打错了，或固件比本仓库新）`,
    });
  } else {
    if (meta.dispatchable !== true) {
      /*
       * 上游的 `command` 属性**不能**当否定判据用。
       *
       * 它是文档标记，而不是"飞控会不会执行"的声明：`MAV_CMD_DO_START_MAG_CAL`
       * （罗盘校准，界面上的常用功能）在上游就没有这个标记，但 ArduPilot 确实在
       * `GCS_Common` 里实现了它。拿它做拦截，结果是"按钮灰了，但其实能用" ——
       * 这种假阴性比让飞控回一次 `UNSUPPORTED` 难查得多（见 `isSupportedBy` 的说明）。
       */
      warnings.push(
        '上游定义里没有把这条命令标记为可直接下发（command="true"），飞控可能回 UNSUPPORTED',
      );
    }

    const support = isSupportedBy(kind, meta.id);
    if (!support.supported) {
      blockers.push({ code: 'vehicle-unsupported', text: support.note });
    } else if (support.basis === 'assumed') {
      // 没有逐机型核对过就放行：必须让界面知道这是"未校验"而不是"已确认支持"
      warnings.push(`机型支持未校验（${support.note}），飞控可能回 UNSUPPORTED`);
    }

    if (ARMED_REQUIRED.has(meta.id) && !state.armed) {
      blockers.push({
        code: 'not-armed',
        text: '未解锁：这条命令在未解锁时下发不会生效，请先解锁电机',
      });
    }
    if (DISARMED_REQUIRED.has(meta.id) && state.armed) {
      blockers.push({
        code: 'armed',
        text: '已解锁：这条命令只在未解锁时允许（电机/舵机测试与校准会上锁保护），请先上锁',
      });
    }
    if (POSITION_REQUIRED.has(meta.id) && state.latDeg === null) {
      blockers.push({
        code: 'no-position',
        text: '缺少定位：飞控还没有可用的位置估计（GPS 未定位或 EKF 未收敛），这条命令无法执行',
      });
    }

    const risk = meta.risk;
    if (context.enforceConfirmation && needsConfirmation(risk) && request.confirmed !== true) {
      blockers.push({
        code: 'needs-confirmation',
        text: `这是"${COMMAND_RISK_LABELS[risk]}"的命令，需要二次确认后才会下发`,
      });
    }
  }

  // 链路检查放在机型检查之后：断链时"链路断开"才是用户该看到的第一个原因
  if (state.linkState !== 'online') {
    blockers.unshift({
      code: 'link-offline',
      text: `链路不可用（${LINK_STATE_LABELS[state.linkState]}），指令发不出去`,
    });
  } else if (!state.online) {
    blockers.unshift({
      code: 'vehicle-offline',
      text: '这台车的心跳已经超时（组件可能已断电或掉线），先确认链路再下发指令',
    });
  }

  if (context.commandNumberBusy) {
    blockers.push({
      code: 'command-busy',
      text: '同编号的命令正在执行中，而 MAVLink 的确认不带请求 id —— 现在下发会让两次的回应无法区分',
    });
  }

  const hasLocation = meta?.hasLocation === true;
  const wantsInt = hasLocation && request.forceLong !== true;
  const viaInt = wantsInt && context.commandIntAvailable;
  if (wantsInt && !viaInt) {
    warnings.push(
      '这条命令带经纬度，将以 COMMAND_LONG 的 float32 下发：坐标精度会退化到米级' +
        (context.commandIntAvailable ? '' : '（飞控未上报 COMMAND_INT 能力）'),
    );
  }

  return { blockers, warnings, viaInt };
}
